import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function SecurityAwarenessEducation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6 min-h-screen">
      {/* BACK BUTTON */}
      <Link
        to="/services/security-consulting-advisory"
        className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
      >
        <ArrowLeft size={26} />
      </Link>

      <div className="max-w-3xl mx-auto text-left w-full">
        {/* IMAGE */}
        <div className="flex justify-center mb-10">
          <img
            src="https://static.wixstatic.com/media/ba321a_7b5497277af0430c8588f840bd355bdd~mv2.jpg"
            alt="Security Awareness"
            className="w-44 h-44 rounded-full object-cover shadow-xl"
          />
        </div>

        {/* TITLE */}
        <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
          Security Awareness and Education
        </h1>

        {/* INTRO */}
        <p className="text-white/80 leading-relaxed mb-10">
          People are either an organisation's first line of defence, or its
          greatest vulnerability. That’s why{" "}
          <b>Security Awareness and Education</b>
          has never been more important.
        </p>

        {/* BENEFITS */}
        <h2 className="text-xl font-bold mb-4">
          The <b>benefits </b>of a cyber-informed workforce are:
        </h2>

        <div className="space-y-4 text-white/80 mb-12">
          <p>
            <b>Reduction in cyber security risk</b>, as users become more adept
            at identifying malware and malicious emails, using devices and
            handling information securely, and navigating the Internet safely.
          </p>

          <p>
            <b>Increased reporting of cyber security events and incidents</b>,
            reducing false negatives and the average time between incident
            detection and response.
          </p>

          <p>
            <b>Reputational assurance</b> through knowing your information is
            being stored, transferred, and handled appropriately by staff who
            understand their roles and your organisation’s cyber policies.
          </p>
        </div>

        {/* APPROACH */}
        <h2 className="text-xl font-bold mb-4">Our Approach</h2>
        <p className="text-white/80 leading-relaxed mb-12">
          We understand that culture doesn’t change overnight. It is vital that
          your staff understand how cyber security can be an enabler, not an
          obstacle, to working effectively. Our approach therefore encompasses
          comprehensive learning needs analysis and business consultation to
          ensure our campaigns are as effective as possible.
        </p>

        {/* EXPERIENCE */}
        <h2 className="text-xl font-bold mb-4">Our Experience</h2>
        <p className="text-white/80 leading-relaxed mb-12">
          We have built and delivered data-driven, high-impact campaigns for
          some of the world's largest and most high profile organisations. Our
          programs cover the gamut from education of the C-suite on spear
          phishing and regulatory compliance to SOC orchestration and
          automation.
        </p>

        {/* KEY SERVICES */}
        <h2 className="text-xl font-bold mb-4">Key Services</h2>
        <p className="text-white/80 mb-4">
          Our most utilised Security Awareness and Education services are:
        </p>

        <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
          <li>
            <b>Education strategies</b>, for tracking and improving awareness
            and engagement via tailored combinations of the services below.
          </li>

          <li>
            <b>Security awareness baselining</b>, through formative assessment
            techniques benchmarked against the NIST NICE Framework.
          </li>

          <li>
            <b>Security awareness training</b>, for all staff, personnel with
            elevated privileges or risk, third parties, executives, travel and
            device security, as well as regular refresher sessions.
          </li>

          <li>
            <b>Creative content development</b>, such as blogs, posters, comics,
            videos, or choose-your-own-adventure courses to provide high impact,
            low effort reinforcement of key security concepts.
          </li>

          <li>
            <b>Validation exercises</b>, such as phishing simulation tests, user
            surveys and threat modelling workshops.
          </li>

          <li>
            Designing and facilitating{" "}
            <b>tailored crisis or breach response </b>
            simulations to stress test an organisation’s people and process and
            identify shortcomings in a safe and controlled environment.
          </li>
        </ul>

        {/* CTA */}
        <p className="text-white/80 mb-8">
          Whether big or small, get in touch with us today to learn how we can
          build an awareness campaign tailored to your exact needs and risk
          profile.
        </p>

        <div className="flex justify-center">
          <Link
            to="/contact"
            className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
          >
            Find Out More
          </Link>
        </div>
      </div>
    </section>
  );
}
